import { FavouriteService } from '../../FavouriteService';
import { createRequestBuilder, HttpRequest } from '@quickplay/foundation';
import { PlatformAuthorizer, AuthorizationUtil } from '@quickplay/platform-authorizer';
import { FavouriteRecord } from '../model/FavouriteRecord';
import { FavouriteCount, SortBy, SortOrder, FavouriteResponse } from './FavouriteResponse';
import { FavouriteRequest } from './FavouriteRequest';

export function createFavouriteServiceImpl(
  favouriteEndPointUrl: string,
  platformAuthorizer: PlatformAuthorizer,
): FavouriteService {
  const favouriteService: FavouriteService = {
    getFavourites(
      pageNumber?: number,
      pageSize?: number,
      sortBy?: SortBy,
      sortOrder?: SortOrder,
    ): Promise<FavouriteRecord[]> {
      return getFavourites(pageNumber, pageSize, sortBy, sortOrder);
    },

    getFavourite(contentId: string): Promise<FavouriteRecord> {
      return getFavourite(contentId);
    },

    putFavourite(contentId: string): Promise<void> {
      return putFavourite(contentId);
    },

    deleteFavourite(contentId: string): Promise<void> {
      return deleteFavourite(contentId);
    },

    getFavouriteCount(): Promise<FavouriteCount> {
      return getFavouriteCount();
    },
  };

  async function getFavourites(
    pageNumber?: number,
    pageSize?: number,
    sortBy?: SortBy,
    sortOrder?: SortOrder,
  ): Promise<FavouriteRecord[]> {
    try {
      const queryParameters: any = {};
      if (pageNumber) queryParameters.pageNumber = pageNumber;
      if (pageSize) queryParameters.pageSize = pageSize;
      if (sortBy) queryParameters.sort = sortBy;
      if (sortOrder) queryParameters.order = sortOrder;

      const request = createRequestBuilder()
        .url(favouriteEndPointUrl)
        .path(`/user/favorite/list`)
        .get()
        .queryParameters(queryParameters)
        .build();

      const result = await AuthorizationUtil.makeAuthorizedRequest<any, FavouriteRecord[]>(request, platformAuthorizer);

      return getFavouriteRecords(result);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getFavourite(contentId: string): Promise<FavouriteRecord> {
    try {
      const path = `/user/favorite/lookup/${contentId}`;
      const result = await AuthorizationUtil.makeAuthorizedRequest<any, FavouriteResponse>(
        buildGetRequest(path),
        platformAuthorizer,
      );

      return {
        contentId: result.data.itemId,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function putFavourite(contentId: string): Promise<void> {
    try {
      const request = buildPutFavouriteRequest(contentId);
      await AuthorizationUtil.makeAuthorizedRequest<any, void>(request, platformAuthorizer);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function deleteFavourite(contentId: string): Promise<void> {
    try {
      await AuthorizationUtil.makeAuthorizedRequest<any, void>(
        buildDeleteFavouriteRequest(contentId),
        platformAuthorizer,
      );

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getFavouriteCount(): Promise<FavouriteCount> {
    try {
      const path = `/user/favorite/count`;
      const response = await AuthorizationUtil.makeAuthorizedRequest<any, FavouriteCount>(
        buildGetRequest(path),
        platformAuthorizer,
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function buildGetRequest(path: string): HttpRequest<any> {
    return createRequestBuilder().url(favouriteEndPointUrl).path(path).get().build();
  }

  function buildPutFavouriteRequest(contentId: string): HttpRequest<any> {
    const favouriteRequest: FavouriteRequest = {
      itemId: contentId,
    };
    return createRequestBuilder<FavouriteRequest>()
      .url(favouriteEndPointUrl)
      .path('/user/favorite/create')
      .put(favouriteRequest)
      .build();
  }

  function buildDeleteFavouriteRequest(contentId: string): HttpRequest<any> {
    return createRequestBuilder()
      .url(favouriteEndPointUrl)
      .path(`/user/favorite/delete/${contentId}`)
      .delete('')
      .build();
  }

  function getFavouriteRecords(result: any): FavouriteRecord[] {
    const favouriteRecords: FavouriteRecord[] = [];
    result.data.forEach((favouriteData: FavouriteResponse) => {
      const favouriteRecord: FavouriteRecord = {
        contentId: favouriteData.itemId,
        timestamp: favouriteData.ut,
      };
      favouriteRecords.push(favouriteRecord);
    });
    return favouriteRecords;
  }

  return favouriteService;
}
